import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import type { GrantReportInput } from '@zoxo/shared';

import { Flag } from '@/components/icons';
import { EditDialog } from '@/components/ui/edit-dialog';
import { useSnackbar } from '@/components/ui/magic-snackbar';
import { Textarea } from '@/components/ui/textarea';
import { api } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { ASIDE_BUTTON_OUTLINE } from './grant-parts';

/**
 * 상세 우측 카드 맨 아래 「잘못된 내용 신고」 — **로그인한 회원에게만 보인다.**
 * 누르면 작은 팝업(제목 · 설명 · textarea). 보내면 서버가 Slack 으로 바로 넘긴다. DB 에 남지 않는다.
 */
export function GrantReportButton({ grantId }: { grantId: string }) {
  const { isAuthenticated } = useAuth();
  const { showSnackbar } = useSnackbar();
  const [open, setOpen] = useState(false);
  const [text, setText] = useState('');
  const send = useMutation({
    mutationFn: (input: GrantReportInput) => api.post<void>(`/grants/${grantId}/report`, input),
  });
  if (!isAuthenticated) return null;

  const close = (o: boolean) => { if (!o) { setOpen(false); send.reset(); } };
  return (
    <>
      <button type="button" onClick={() => { setText(''); setOpen(true); }} className={ASIDE_BUTTON_OUTLINE}>
        <Flag className="size-3.5" />
        잘못된 내용 신고
      </button>
      <EditDialog
        open={open}
        onOpenChange={close}
        title="잘못된 내용 신고"
        description="지원사업 내용이 잘못 분류된 것을 포착했을때 신고하는 기능"
        canSave={text.trim().length > 0}
        saving={send.isPending}
        saveLabel="신고"
        error={send.isError ? '보내지 못했다. 잠시 후 다시 시도하자.' : null}
        onSave={() =>
          send.mutate({ body: text.trim() }, {
            onSuccess: () => { setOpen(false); showSnackbar('신고를 보냈다. 확인 후 고친다.', { type: 'success' }); },
          })
        }
      >
        <Textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          rows={5}
          maxLength={2000}
          autoFocus
          placeholder="무엇이 잘못됐는지 작성"
        />
      </EditDialog>
    </>
  );
}
